import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { initials } from "@/lib/brand";
import { UserPlus, Trash2, Mail, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", cls: "bg-orange-50 text-orange-700" },
  team: { label: "Staff", cls: "bg-sky-50 text-sky-700" },
  client: { label: "Client", cls: "bg-violet-50 text-violet-700" },
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "team", client_id: "" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    if (form.role === "client" && !form.client_id) return toast.error("Select a client for client users");
    setSaving(true);
    try {
      const { data } = await api.post("/users", {
        name: form.name, email: form.email, role: form.role,
        client_id: form.role === "client" ? form.client_id : null,
      });
      setCreated(data);
      toast.success(data.welcome_email_sent ? "User created — welcome email sent" : "User created");
      setForm({ name: "", email: "", role: "team", client_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || "—";

  const closeDialog = () => { setOpen(false); setCreated(null); };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Team &amp; Users</h1>
          <p className="text-slate-500 text-sm mt-1">{users.length} accounts · add admins, staff or client logins</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreated(null); }}>
          <DialogTrigger asChild>
            <Button className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="add-user-btn">
              <UserPlus size={16} className="mr-1.5" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            {!created ? (
              <>
                <DialogHeader><DialogTitle className="font-display">Add a user</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Full name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Email (login)</Label>
                    <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" placeholder="name@company.com" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Role</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="team">Staff</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.role === "client" && (
                      <div><Label className="text-xs uppercase tracking-wider text-slate-500">Client</Label>
                        <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                          <SelectTrigger className="mt-1.5" data-testid="user-client-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1"><Mail size={12} /> A temporary password will be generated and emailed to them with a login link.</p>
                </div>
                <DialogFooter>
                  <Button onClick={create} disabled={saving} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="submit-user-btn">
                    {saving ? "Creating…" : "Create & send invite"}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-600" /> User created</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    {created.welcome_email_sent
                      ? "A welcome email with these credentials was sent to the user."
                      : "Email is not configured — share these credentials manually."}
                  </p>
                  <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm space-y-2">
                    <p><span className="text-slate-500">Email:</span> <b>{created.email}</b></p>
                    {created.temp_password && (
                      <p className="flex items-center gap-2">
                        <span className="text-slate-500">Temp password:</span>
                        <code className="bg-white border border-slate-200 rounded px-2 py-0.5" data-testid="temp-password-value">{created.temp_password}</code>
                        <button onClick={() => { navigator.clipboard.writeText(created.temp_password); toast.success("Copied"); }} className="text-slate-400 hover:text-slate-700"><Copy size={14} /></button>
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={closeDialog} variant="outline" data-testid="done-user-btn">Done</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 divide-y">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid="user-row">
            <Avatar className="h-9 w-9"><AvatarFallback className="text-white text-xs" style={{ background: "var(--tenant-primary)" }}>{initials(u.name)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
              <p className="text-xs text-slate-400 truncate">{u.email}{u.role === "client" && ` · ${clientName(u.client_id)}`}</p>
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded ${ROLE_META[u.role]?.cls}`}>{ROLE_META[u.role]?.label || u.role}</span>
            {u.email_alerts === false && <span className="text-[10px] text-slate-400">alerts off</span>}
            <button onClick={() => remove(u)} className="text-slate-300 hover:text-red-600 ml-1" data-testid="delete-user-btn"><Trash2 size={16} /></button>
          </div>
        ))}
        {users.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No users yet.</p>}
      </Card>
    </div>
  );
}
